import React, { FC } from "react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { ThemeColorsKeys } from "../../styles/tokens/colors";
import { useBackgroundColor } from "../../utils";
import { Stack } from "../stack/Stack";
import { SectionContextProvider } from "../../context/SectionContext";
import { Container } from "../container/Container";
import { StyledTitleBlock } from "../titleBlock/TitleBlock";
import { StyledAccordion } from "../accordion/Accordion";
import { StyledLinkList } from "../linkList/LinkList";
import { StyledTickList } from "../tickList/TickList";
import { StyledProcess } from "../process/Process";
import { StyledHtml } from "../html/Html";

// Interface

export interface SectionProps {
  children: React.ReactNode;
  as?: any;
  backgroundColor?: ThemeColorsKeys;
  narrow?: boolean;
  id?: string;
}

type StyledSectionProps = Pick<SectionProps, "backgroundColor">;

// Styles

export const StyledSection = styled.div<StyledSectionProps>`
  ${({ theme: { space }, backgroundColor }) => css`
    padding: ${space.fluidLargeToXxxxxLarge} 0;
    overflow: hidden;

    // Background and color

    ${backgroundColor && useBackgroundColor(backgroundColor)};

    // Spacing between sibling sections

    &[data-background-color=${backgroundColor}] + [data-background-color=${backgroundColor}] {
      padding-top: 0;
    }
  `};
`;

const StyledStack = styled.div`
  ${({ theme: { space } }) => css`
    /* background: pink; */

    > * + * {
      margin-top: ${space.fluidLargeToXxLarge};
    }

    // Adjust spacing under certain components


    // Title blocks

    > ${StyledTitleBlock} + ${StyledAccordion} {
      margin-top: ${space.fluidMediumToLarge};
    }

    > ${StyledTitleBlock} + ${StyledLinkList} {
      margin-top: ${space.fluidMediumToLarge};
    }

    > ${StyledTitleBlock} + ${StyledTickList} {
      margin-top: ${space.fluidMediumToLarge};
    }

    > ${StyledTitleBlock} + ${StyledProcess} {
      margin-top: ${space.fluidMediumToLarge};
    }

    // Html blocks

    
    > ${StyledHtml} + ${StyledAccordion} {
      margin-top: ${space.fluidMediumToLarge};
    }

    > ${StyledHtml} + ${StyledLinkList} {
      margin-top: ${space.fluidMediumToLarge};
    }

    > ${StyledHtml} + ${StyledTickList} {
      margin-top: ${space.fluidMediumToLarge};
    }

    > ${StyledHtml} + ${StyledProcess} {
      margin-top: ${space.fluidMediumToLarge};
    }
  `};
`;

// JSX

export const Section: FC<React.PropsWithChildren<SectionProps>> = ({
  as = "section",
  children,
  backgroundColor = "white",
  narrow,
  id,
  ...rest
}) => (
  <StyledSection
    backgroundColor={backgroundColor}
    data-background-color={backgroundColor}
    as={as}
    id={id?.toLowerCase()?.replace(/\s+/g, "-")}
    {...rest}
  >
    <SectionContextProvider
      value={{ sectionBackground: backgroundColor, sectionId: id }}
    >
      <Container width={narrow ? "narrow" : "standard"}>
        <StyledStack>{children}</StyledStack>
      </Container>
    </SectionContextProvider>
  </StyledSection>
);

export default Section;
